import { Grid, Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans, useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { PostPreview, type PostPreviewProps } from '#modules/finder/components/PostPreview';
import { SessionCalendar } from '#modules/finder/features/posts/components/Discover/SessionCalendar';
import { OAuthProvider } from '#modules/user/features/auth/constants/providers';
import { DashboardLayout } from '#modules/user/features/dashboard/components/DashboardLayout';
import { Feed } from '#modules/user/features/dashboard/components/Feed';
import { PlayerMetaChips } from '#modules/user/features/profile/components/PlayerMetaChips';
import { Profile } from '#modules/user/features/profile/components/Profile';
import { AccountCard } from '#modules/user/features/profile/components/Settings/Accounts/AccountCard';
import { Gender } from '#modules/user/features/profile/types';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { PresenceLine } from '#features/presence/components/PresenceLine';
import { Button, PageHeading, Panel } from '#features/ui';
import { Avatar } from '#features/ui/components/Avatar';
import { GamePresence } from '#features/ui/components/GamePresence';
import { Preview as GamePresenceShowcase } from '#features/ui/components/GamePresence/GamePresence.showcase';
import { PersonRow } from '#features/ui/components/PersonRow';
import { PlayerStatLine } from '#features/ui/components/PlayerStatLine';
import { SettingsCard } from '#features/ui/components/SettingsCard';

/** Изолированные данные для приёмки статуса: никаких чтений или записей API внутри превью. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/game-presence')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    surface:
      search.surface === 'surfaces'
        ? 'surfaces'
        : search.surface === 'profile'
          ? 'profile'
          : search.surface === 'feed'
            ? 'feed'
            : search.surface === 'card'
              ? 'card'
              : 'overview',
    state: ['long', 'another', 'recent', 'none', 'loading'].includes(String(search.state))
      ? String(search.state)
      : 'match',
    own: search.own === true || search.own === 'true',
  }),
  component: PresencePreviewRoute,
});

function PresencePreviewRoute() {
  const { lang, surface, state, own } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  return (
    <I18nProvider i18n={i18n}>
      <PresencePreview
        key={`${lang}:${surface}:${state}:${own}`}
        surface={surface}
        state={state}
        own={own}
      />
    </I18nProvider>
  );
}

const GAME = 'presence-preview-game';
const DOTA = 'presence-preview-dota';
const USER = 'presence-preview-user';
const longName = 'Counter-Strike 2: Premier Competitive Edition';
const activity = {
  playingNow: true,
  gameId: GAME,
  currentGameName: 'Apex Legends',
  currentSource: 'XBOX' as const,
  platform: 'XBOX',
  lastPlayedGameName: null,
  lastPlayedSource: null,
  lastPlayedAt: null,
};
const post: PostPreviewProps = {
  id: 'presence-preview-post',
  userId: USER,
  text: 'ggggggggggggg',
  platformsIds: ['xbox'],
  adult: false,
  mic: true,
  commentsCount: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  bumpedAt: new Date().toISOString(),
  gameId: GAME,
  favourite: false,
  qr: false,
  discordGuildName: null,
  discordVoiceChannel: null,
  authorGameActivity: activity,
  activeSession: null,
  languages: [{ code: 'en', name: 'EN', nativeName: 'English' }],
  data: [],
  gameData: { id: GAME, name: 'Apex Legends', logo: null, platformsIds: ['xbox'] },
  userData: {
    id: USER,
    displayName: 'juggers',
    avatar: null,
    lastVisit: null,
    gender: Gender.MALE,
    birthday: '2013-08-01',
    socials: [],
    country: null,
  },
  discordMembersCount: 0,
  discordPublished: false,
  bookmarkDisabled: true,
};

function PresencePreview({
  surface,
  state,
  own,
}: {
  surface: string;
  state: string;
  own: boolean;
}) {
  const { t } = useLingui();
  const fixtureActivity =
    state === 'none'
      ? null
      : {
          ...activity,
          playingNow: state !== 'recent',
          gameId: state === 'another' ? DOTA : GAME,
          currentGameName:
            state === 'long' ? longName : state === 'another' ? 'Dota 2' : 'Apex Legends',
          lastPlayedGameName: state === 'recent' ? 'Dota 2' : null,
          lastPlayedAt: state === 'recent' ? new Date(Date.now() - 3600000).toISOString() : null,
        };
  const fixturePost: PostPreviewProps = {
    ...post,
    authorGameActivity: fixtureActivity,
    gameData: {
      id: GAME,
      name: state === 'long' ? longName : 'Apex Legends',
      logo: null,
      platformsIds: ['xbox'],
    },
  };
  const [client] = useState(() => {
    const value = new QueryClient();
    const defaults = value.defaultQueryOptions.bind(value);
    value.defaultQueryOptions = (options) => ({
      ...defaults(options),
      enabled: false,
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      queryFn: async () => {
        throw new Error('Isolated presence preview cannot access the API');
      },
    });
    const mutations = value.defaultMutationOptions.bind(value);
    value.defaultMutationOptions = (options) => ({
      ...mutations(options),
      mutationFn: async () => {
        throw new Error('Isolated presence preview cannot mutate the API');
      },
    });
    value.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: own ? USER : 'presence-preview-viewer',
      displayName: own ? 'juggers' : 'viewer',
      oauth: {},
      gameIds: [],
      interestsIds: [],
      onboarding: { profileSaved: true, postCreated: true },
    });
    value.setQueryData(
      ['games'],
      [
        {
          __typename: 'Game',
          id: GAME,
          name: state === 'long' ? longName : 'Apex Legends',
          icon: null,
          providers: [],
        },
        { __typename: 'Game', id: DOTA, name: 'Dota 2', slug: 'dota-2', icon: null, providers: [] },
      ]
    );
    value.setQueryData(['gameActivity', USER, DOTA], {
      ...activity,
      gameId: DOTA,
      currentGameName: 'Dota 2',
    });
    value.setQueryData(['dotaStats', USER, DOTA], {
      steamPlaytime: { minutes: 120000 },
      dotaMatches: [],
    });
    value.setQueryData(['platforms'], [{ id: 'xbox', name: 'Xbox' }]);
    value.setQueryData(['presence', [USER]], [{ userId: USER, status: 'ONLINE' }]);
    const currentGameName =
      state === 'long' ? longName : state === 'another' ? 'Dota 2' : 'Apex Legends';
    value.setQueryData(
      ['gameActivity', 'juggers', undefined],
      state === 'none'
        ? null
        : {
            ...activity,
            playingNow: state !== 'recent',
            gameId: state === 'long' ? null : state === 'another' ? DOTA : GAME,
            currentGameName,
            lastPlayedGameName: state === 'recent' ? 'Dota 2' : null,
            lastPlayedAt: state === 'recent' ? new Date(Date.now() - 3600000).toISOString() : null,
          }
    );
    value.setQueryData(['profile', 'juggers'], {
      __typename: 'UserProfile',
      id: USER,
      displayName: 'juggers',
      avatar: null,
      about: '',
      birthday: '2013-08-01',
      gender: Gender.MALE,
      gameIds: [],
      platformsIds: ['xbox'],
      languages: post.languages,
      interests: [],
      socials: [],
      country: null,
      lastVisit: new Date().toISOString(),
    });
    value.setQueryData(['follow', 'juggers'], {
      state: 'NONE',
      followers: 3,
      friends: 1,
      following: 4,
    });
    value.setQueryData(['profile-user-sessions', USER], {
      pages: [{ records: [] }],
      pageParams: [0],
    });
    const emptyLibrary = {
      user: {
        profileByDisplayNameId: {
          steamGames: [],
          steamGamesTotal: 0,
          xboxGames: [],
          xboxGamesTotal: 0,
        },
      },
    };
    value.setQueryData(['steam-games', 'juggers'], emptyLibrary);
    value.setQueryData(['xbox-games', 'juggers'], emptyLibrary);
    value.setQueryData(['feed'], {
      pages: [{ records: [{ data: { ...fixturePost, __typename: 'Post' } }], hasMore: false }],
      pageParams: [0],
    });
    return value;
  });
  const availability = {
    userId: USER,
    status: 'ONLINE' as const,
    playingNow: true,
    matchesGame: false,
    gameName: 'Apex Legends',
    rank: 'ONLINE' as const,
  };

  return (
    <QueryClientProvider client={client}>
      {surface === 'surfaces' ? (
        <DashboardLayout isAuth>
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} p={4} minW={0}>
            <Stack gap={4} minW={0}>
              <PageHeading>{t`Ways to sign in`}</PageHeading>
              {[
                OAuthProvider.DISCORD,
                OAuthProvider.GOOGLE,
                OAuthProvider.STEAM,
                OAuthProvider.EPIC_GAMES,
                OAuthProvider.RIOT_GAMES,
              ].map((provider) => (
                <AccountCard
                  key={provider}
                  provider={provider}
                  id={provider === OAuthProvider.GOOGLE ? undefined : `preview-${provider}`}
                  displayName="Nova"
                  compact
                  statsVisible
                  discordPresence
                  hiddenGameIds={[]}
                  statsDisabled={false}
                  onToggleStats={() => undefined}
                  onToggleDiscordPresence={() => undefined}
                />
              ))}
              <SettingsCard>
                <SettingsCard.Header title={t`Notifications`} />
                <SettingsCard.Row title={t`Email`} />
                <SettingsCard.Row title={t`Browser push`} />
              </SettingsCard>
            </Stack>
            <SessionCalendar
              sessions={Array.from(
                { length: 14 },
                (_, index) =>
                  ({
                    ...fixturePost,
                    id: `calendar-${index}`,
                    activeSession: {
                      id: `session-${index}`,
                      startedAt: new Date(Date.now() + index * 3600000).toISOString(),
                      expiredAt: new Date(Date.now() + (index + 1) * 3600000).toISOString(),
                      status: index === 0 ? 'LIVE' : 'UPCOMING',
                      teamLimit: 4,
                      acceptedMembersCount: 1,
                      voiceMode: 'PLATFORM',
                      members: [],
                    },
                  }) as never
              )}
            />
          </Grid>
        </DashboardLayout>
      ) : surface === 'card' ? (
        <DashboardLayout isAuth>
          <PostPreview {...fixturePost} placeholder={state === 'loading'} />
        </DashboardLayout>
      ) : surface === 'feed' ? (
        <DashboardLayout isAuth>
          <Feed />
        </DashboardLayout>
      ) : surface === 'profile' ? (
        <Stack p={4} maxW="6xl" mx="auto">
          <Profile playerId="juggers" />
        </Stack>
      ) : (
        <Stack gap={6} p={4} maxW="6xl" mx="auto">
          <PageHeading>{t`Playing now`}</PageHeading>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
            {[true, false].map((withIcon) => (
              <Panel key={String(withIcon)} variant="container" p={4}>
                <Stack gap={3}>
                  <Text textStyle="caption" color="fg.muted">
                    {withIcon ? t`With icon` : t`Without icon`}
                  </Text>
                  <Text textStyle="title">juggers</Text>
                  <GamePresence
                    presence={{ state: 'MATCH', gameName: 'Apex Legends' }}
                    showGameGlyph={withIcon}
                  />
                  <GamePresence
                    presence={{ state: 'MATCH', gameName: 'Apex Legends' }}
                    variant="inline"
                    showGameGlyph={withIcon}
                  />
                  <GamePresence
                    presence={{ state: 'RECENT', gameName: 'Dota 2', playedAgo: t`1 hour ago` }}
                    showGameGlyph={withIcon}
                  />
                </Stack>
              </Panel>
            ))}
          </Grid>
          <Grid templateColumns={{ base: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }} gap={4}>
            <Stack gap={4} minW={0}>
              <Panel variant="container" p={4}>
                <PersonRow userId={USER} displayName="juggers" availability={availability} />
                <PersonRow
                  userId="presence-preview-long"
                  displayName="LongPlayerNameForPresence"
                  availability={{
                    ...availability,
                    userId: 'presence-preview-long',
                    status: 'OFFLINE',
                    gameName: longName,
                  }}
                />
                <PresenceLine signals={{ status: 'ONLINE' }} />
                <PresenceLine signals={{ status: 'AWAY' }} />
                <PresenceLine
                  signals={{
                    lastPlayedGameName: 'Dota 2',
                    lastPlayedAt: new Date(Date.now() - 3600000).toISOString(),
                  }}
                />
              </Panel>
              <PostPreview {...fixturePost} placeholder={state === 'loading'} />
            </Stack>
            <Panel variant="container" p={4} maxW="80" alignSelf="start">
              <Stack gap={3} minW={0}>
                <Avatar userId={USER} name="juggers" size="xl" presenceStatus="ONLINE" />
                <Text textStyle="section">juggers</Text>
                <GamePresence presence={{ state: 'IN_GAME', gameName: 'Apex Legends' }} />
                <PlayerMetaChips age={13} gender={Gender.MALE} languages={post.languages} />
                <Button>
                  <Trans>Chat</Trans>
                </Button>
                <GamePresence presence={{ state: 'IN_GAME', gameName: longName }} />
                <PlayerStatLine seed={USER} gameId={DOTA} />
              </Stack>
            </Panel>
          </Grid>
          <Panel variant="container" p={4}>
            <GamePresenceShowcase />
          </Panel>
        </Stack>
      )}
    </QueryClientProvider>
  );
}
